import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Modal, Text, Tooltip} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import {QueryingChartContent} from 'charts/chart-content/ChartContent';
import {isNodeTesting} from 'lib/Browser';
import {SaveChartToEntryButton} from 'llm/charts/SaveChartToEntryButton';
import {useLLMMarkdownDependencies} from 'llm/markdown/LLMMarkdownDependenciesContext';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

const LLM_CHART_HEIGHT = 450;

const useChartStyles = createStyles((theme) => ({
  chartContainer: {
    width: '100%',
    height: LLM_CHART_HEIGHT,
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.md,
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  chartToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    flexShrink: 0,
  },
  chartBody: {
    flex: 1,
    minHeight: 0,
  },
  modalContent: {
    maxWidth: '95vw',
    height: `calc(100vh - 48px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  modalBody: {
    backgroundColor: theme.colors.gray[3],
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  expandedChartContent: {
    width: '100%',
    flex: 1,
    minHeight: 400,
    borderRadius: theme.radius.md,
    overflow: 'hidden',
  },
}));

export default function InlineChart({idValue}: Record<string, unknown>): JSX.Element {
  const chartId = idValue as string;
  const {chatSessionId, canSaveChartsToEntry} = useLLMMarkdownDependencies();
  const [isChartExpanded, setIsChartExpanded] = useState(false);
  const {classes, theme} = useChartStyles();

  const handleExpandClick = useCallback(() => {
    setIsChartExpanded(true);
  }, []);

  const handleCloseExpanded = useCallback(() => {
    setIsChartExpanded(false);
  }, []);

  return (
    <div className={classes.chartContainer} data-chart-id={chartId}>
      <div className={classes.chartToolbar}>
        {canSaveChartsToEntry && <SaveChartToEntryButton chartId={chartId} chatSessionId={chatSessionId} />}
        <Tooltip label="Expand chart" withArrow={true}>
          <span>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MINI}
              icon="expand-right-full"
              aria-label="Expand chart"
              iconAltText="Expand chart"
              onClick={handleExpandClick}
              {...getTestProps({component: 'ChartComponent', element: 'expandButton'})}
            />
          </span>
        </Tooltip>
      </div>
      <div className={classes.chartBody}>
        <QueryingChartContent chartId={chartId} />
      </div>
      <Modal
        opened={isChartExpanded}
        onClose={handleCloseExpanded}
        title={
          <Text color={theme.colors.gray[9]} size={theme.fontSizes.lg} fw={600}>
            Chart
          </Text>
        }
        withCloseButton={true}
        centered={true}
        size="95vw"
        xOffset="2.5vw"
        closeOnClickOutside={true}
        closeOnEscape={true}
        withinPortal={!isNodeTesting}
        closeButtonProps={{
          'aria-label': 'Close expanded chart',
        }}
        classNames={{
          content: classes.modalContent,
          body: classes.modalBody,
        }}
      >
        <Box className={classes.expandedChartContent}>
          <QueryingChartContent chartId={chartId} />
        </Box>
      </Modal>
    </div>
  );
}
